import React, { useState, useEffect } from 'react'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Grid
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Games as GamesIcon,
  Favorite as FavoriteIcon,
  Star as StarIcon
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { db } from '../firebase/config'
import { collection, getDocs, doc, setDoc, getDoc } from 'firebase/firestore'
import toast from 'react-hot-toast'

const SavedWordsMatch = ({ userData, onBack, userLevel }) => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [currentLevel, setCurrentLevel] = useState(userLevel || 'A1')
  
  // Game state
  const [allWords, setAllWords] = useState([])
  const [gameWords, setGameWords] = useState([])
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [score, setScore] = useState(0)
  const [lives, setLives] = useState(3)
  const [totalQuestions, setTotalQuestions] = useState(0)
  const [gameStarted, setGameStarted] = useState(false)
  const [gameOver, setGameOver] = useState(false)
  const [gameComplete, setGameComplete] = useState(false)
  
  // Question state
  const [currentWord, setCurrentWord] = useState(null)
  const [options, setOptions] = useState([])
  const [correctAnswer, setCorrectAnswer] = useState('')
  const [showFeedback, setShowFeedback] = useState(false)
  const [selectedAnswer, setSelectedAnswer] = useState('')
  const [isCorrect, setIsCorrect] = useState(false)

  const POINTS_PER_CORRECT = 10

  useEffect(() => {
    console.log('SavedWordsMatch useEffect triggered:', {
      user: !!user,
      userData: !!userData,
      userLevel,
      userDataLevel: userData?.level
    })
    
    // Daha basit koşul
    if (user && userData) {
      const level = userLevel || userData.level || 'A1'
      console.log('Setting currentLevel to:', level)
      setCurrentLevel(level)
      loadWordsAndStartGame(level)
    } else {
      console.log('useEffect conditions not met, not loading game')
    }
  }, [user, userData, userLevel])

  const loadWordsAndStartGame = async (level) => {
    console.log('loadWordsAndStartGame called with level:', level)
    
    if (!user) {
      console.log('No user, showing error')
      toast.error('Giriş yapmanız gerekiyor')
      return
    }

    try {
      console.log('Loading saved words for level:', level)

      // Kullanıcının kaydedilen kelimelerini yükle
      const savedWordsRef = collection(db, 'users', user.uid, 'savedWords', level, 'words')
      const savedWordsSnapshot = await getDocs(savedWordsRef)
      
      const words = []
      savedWordsSnapshot.forEach((doc) => {
        const data = doc.data()
        words.push({
          word: data.word,
          meaning: data.meaning
        })
      })

      console.log('Loaded words:', words.length)
      
      if (words.length >= 10) {
        // Tüm kelimeleri kullan
        const shuffledWords = [...words].sort(() => Math.random() - 0.5)
        setAllWords(words)
        setGameWords(shuffledWords)
        setTotalQuestions(words.length)
        setLoading(false)
        startGame(shuffledWords, words)
      } else {
        setLoading(false)
        toast.error(`Bu oyunu oynayabilmek için en az 10 kelime kaydetmiş olmalısınız. Mevcut kelime sayınız: ${words.length}`)
        // onBack() çağırmayalım, kullanıcı manuel olarak geri dönsün
      }
    } catch (error) {
      console.error('Error loading words:', error)
      toast.error('Kelimeler yüklenirken hata oluştu')
      setLoading(false)
      // onBack() çağırmayalım, kullanıcı manuel olarak geri dönsün
    }
  }

  const startGame = (gameWordsArray, allWordsArray) => {
    setCurrentQuestionIndex(0)
    setScore(0)
    setLives(3)
    setGameStarted(true)
    setGameOver(false)
    setGameComplete(false)
    showNextQuestion(0, gameWordsArray, allWordsArray)
  }

  const showNextQuestion = (questionIndex, gameWordsArray, allWordsArray) => {
    if (questionIndex >= gameWordsArray.length) {
      endGame()
      return
    }

    const word = gameWordsArray[questionIndex]
    const correctMeaning = word.meaning
    
    setCurrentWord(word)
    setCorrectAnswer(correctMeaning)
    
    // Seçenekleri oluştur
    createOptions(correctMeaning, allWordsArray)
  }

  const createOptions = (correctMeaning, allWordsArray) => {
    const optionsList = [correctMeaning]
    
    // Yanlış seçenekler için diğer kelimelerden anlamlar al
    const wrongMeanings = []
    for (const word of allWordsArray) {
      const meaning = word.meaning
      if (meaning !== correctMeaning && !wrongMeanings.includes(meaning)) {
        wrongMeanings.push(meaning)
      }
    }
    
    // 4 yanlış seçenek ekle
    const shuffledWrong = [...wrongMeanings].sort(() => Math.random() - 0.5)
    for (let i = 0; i < 4 && i < shuffledWrong.length; i++) {
      optionsList.push(shuffledWrong[i])
    }
    
    // Seçenekleri karıştır
    const shuffledOptions = [...optionsList].sort(() => Math.random() - 0.5)
    setOptions(shuffledOptions)
  }

  const checkAnswer = (selectedAnswer) => {
    setSelectedAnswer(selectedAnswer)
    setShowFeedback(true)
    
    if (selectedAnswer === correctAnswer) {
      // Doğru cevap
      setScore(prev => prev + POINTS_PER_CORRECT)
      setIsCorrect(true)
      toast.success(`Doğru! +${POINTS_PER_CORRECT} puan`)
    } else {
      // Yanlış cevap
      setLives(prev => prev - 1)
      setIsCorrect(false)
      toast.error(`Yanlış! Doğru cevap: ${correctAnswer}`)
    }
    
    // 1.5 saniye sonra bir sonraki soruya geç
    setTimeout(() => {
      setShowFeedback(false)
      const newIndex = currentQuestionIndex + 1
      setCurrentQuestionIndex(newIndex)
      
      if (lives - (selectedAnswer !== correctAnswer ? 1 : 0) <= 0) {
        setGameOver(true)
      } else if (newIndex >= totalQuestions) {
        endGame()
      } else {
        showNextQuestion(newIndex, gameWords, allWords)
      }
    }, 1500)
  }

  const endGame = () => {
    setGameComplete(true)
    saveScore()
  }

  const saveScore = async () => {
    if (!user) return

    try {
      // Android uygulamasındaki gibi levels/{level}/leaderboard/{userId} yapısını kullan
      const leaderboardRef = doc(db, 'levels', currentLevel, 'leaderboard', user.uid)
      const leaderboardDoc = await getDoc(leaderboardRef)
      
      let savedWordsScores = {}
      let currentSavedWordsScore = 0
      
      if (leaderboardDoc.exists()) {
        const data = leaderboardDoc.data()
        savedWordsScores = data.savedWordsScores || {}
        currentSavedWordsScore = data.savedWordsScore || 0
      }
      
      // Timestamp ile yeni skoru ekle
      const timestamp = Date.now().toString()
      if (!savedWordsScores[currentLevel]) {
        savedWordsScores[currentLevel] = {}
      }
      savedWordsScores[currentLevel][timestamp] = score
      
      // Tüm levellerin saved words puanlarını topla (yeni puan dahil)
      const savedWordsTotal = Object.values(savedWordsScores).reduce((total, levelScores) => {
        return total + Object.values(levelScores).reduce((levelTotal, score) => levelTotal + score, 0)
      }, 0)
      
      console.log('SavedWordsScores structure:', savedWordsScores)
      console.log('Calculated total:', savedWordsTotal)
      
      // Kullanıcı bilgilerini al
      const userDoc = await getDoc(doc(db, 'users', user.uid))
      const username = userDoc.exists() ? userDoc.data().username : user?.displayName || 'User'
      const profileImageUrl = userDoc.exists() ? userDoc.data().profileImageUrl : null
      
             // Mevcut diğer oyun puanlarını al
       const existingData = leaderboardDoc.exists() ? leaderboardDoc.data() : {}
       const matchScore = existingData.matchScore || 0
       const scrambleScore = existingData.scrambleScore || 0
       const quizScore = existingData.quizScore || 0
       const listeningWriteScore = existingData.listeningWriteScore || 0
       
       // Toplam puanı hesapla (Android uygulamasındaki gibi)
       const totalScore = matchScore + scrambleScore + quizScore + listeningWriteScore + savedWordsTotal
       
       // Puanı kaydet
       await setDoc(leaderboardRef, {
         savedWordsScores: savedWordsScores,
         savedWordsScore: savedWordsTotal,
         totalScore: totalScore,
         username: username,
         profileImageUrl: profileImageUrl,
         lastUpdated: new Date()
       }, { merge: true })
      
             console.log('Saved Words score saved successfully:', savedWordsTotal)
       console.log('Current score:', score, 'Previous total:', currentSavedWordsScore, 'New total:', savedWordsTotal)
       console.log('Total score calculation:', {
         matchScore,
         scrambleScore,
         quizScore,
         listeningWriteScore,
         savedWordsTotal,
         totalScore
       })
      toast.success('Puanınız kaydedildi!')
    } catch (error) {
      console.error('Error saving score:', error)
      toast.error('Puan kaydedilirken hata oluştu')
    }
  }

  const restartGame = () => {
    // Mevcut puanı kaydet
    saveScore()
    // Oyunu yeniden başlat
    startGame(gameWords, allWords)
  }

  const handleExit = () => {
    onBack()
  }

  if (loading) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <AppBar position="static">
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={onBack} sx={{ mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Kelime Eşleştirme
            </Typography>
          </Toolbar>
        </AppBar>
        <Container maxWidth="md" sx={{ py: 3 }}>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Kelimeler yükleniyor...</Typography>
          </Box>
        </Container>
      </Box>
    )
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* App Bar */}
      <AppBar position="static" sx={{ 
        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={handleExit} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Kelime Eşleştirme
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: 3, pb: 10 }}>
        {/* Header Card */}
        <Card sx={{ 
          mx: 2, 
          mt: 2, 
          borderRadius: 3,
          boxShadow: 4,
          backgroundColor: '#ffffff',
          border: '2px solid #1976d2'
        }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <GamesIcon sx={{ fontSize: 56, color: '#1976d2', mb: 3 }} />
            <Typography variant="h3" sx={{ 
              color: '#1976d2', 
              fontWeight: 'bold',
              mb: 1
            }}>
              Kelime Eşleştirme
            </Typography>
            <Typography variant="h6" sx={{ color: '#1976d2', opacity: 0.8 }}>
              Seviye: {currentLevel}
            </Typography>
          </CardContent>
        </Card>

        {gameStarted && (
          <>
            {/* Game Stats */}
            <Card sx={{ mx: 2, mt: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
              <CardContent sx={{ p: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <StarIcon sx={{ color: '#ffa726', mr: 1 }} />
                      <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                        Puan: {score}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <FavoriteIcon sx={{ color: '#f44336', mr: 1 }} />
                      <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                        Can: {lives}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 'bold', textAlign: 'center' }}>
                      Soru {currentQuestionIndex + 1}/{totalQuestions}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Question Card */}
            {currentWord && (
              <Card sx={{ mx: 2, mt: 2, borderRadius: 3, border: '2px solid #1976d2' }}>
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ 
                    color: '#1976d2', 
                    fontWeight: 'bold',
                    mb: 2
                  }}>
                    {currentWord.word}
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    Bu kelimenin anlamını seçin:
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* Options */}
            <Box sx={{ mx: 2, mt: 2 }}>
              {options.map((option, index) => (
                <Button
                  key={index}
                  fullWidth
                  variant="outlined"
                  sx={{
                    mb: 2,
                    p: 2,
                    borderRadius: 2,
                    borderColor: showFeedback && option === selectedAnswer 
                      ? (isCorrect ? '#4caf50' : '#f44336')
                      : showFeedback && option === correctAnswer
                      ? '#4caf50'
                      : '#1976d2',
                    backgroundColor: showFeedback && option === selectedAnswer 
                      ? (isCorrect ? '#e8f5e8' : '#ffebee')
                      : showFeedback && option === correctAnswer
                      ? '#e8f5e8'
                      : 'white',
                    color: showFeedback && (option === selectedAnswer || option === correctAnswer)
                      ? (option === correctAnswer ? '#4caf50' : isCorrect ? '#4caf50' : '#f44336')
                      : '#1976d2',
                    '&:hover': {
                      backgroundColor: showFeedback ? undefined : '#f5f5f5'
                    },
                    '&:disabled': {
                      backgroundColor: showFeedback && option === selectedAnswer 
                        ? (isCorrect ? '#e8f5e8' : '#ffebee')
                        : showFeedback && option === correctAnswer
                        ? '#e8f5e8'
                        : undefined
                    }
                  }}
                  onClick={() => !showFeedback && checkAnswer(option)}
                  disabled={showFeedback}
                >
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {option}
                  </Typography>
                </Button>
              ))}
            </Box>
          </>
        )}
      </Container>

      {/* Game Over Dialog */}
      <Dialog
        open={gameOver}
        onClose={() => {}}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', color: '#f44336', fontWeight: 'bold' }}>
          Oyun Bitti
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="h4" sx={{ color: '#1976d2', fontWeight: 'bold', mb: 2 }}>
            Toplam Puanınız: {score}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            Maalesef canlarınız bitti. Tekrar denemek ister misiniz?
          </Typography>
        </DialogContent>
                 <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
           <Button
             variant="outlined"
             onClick={() => {
               saveScore()
               handleExit()
             }}
             sx={{ mr: 2, borderRadius: 2 }}
           >
             Çıkış
           </Button>
           <Button
             variant="contained"
             onClick={restartGame}
             sx={{ borderRadius: 2 }}
           >
             Tekrar Oyna
           </Button>
         </DialogActions>
      </Dialog>

      {/* Game Complete Dialog */}
      <Dialog
        open={gameComplete}
        onClose={() => {}}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', color: '#4caf50', fontWeight: 'bold' }}>
          Tebrikler!
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="h4" sx={{ color: '#1976d2', fontWeight: 'bold', mb: 2 }}>
            Toplam Puanınız: {score}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            Tüm soruları tamamladınız! Harika bir performans sergilediniz.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button
            variant="contained"
            onClick={handleExit}
            sx={{ borderRadius: 2 }}
          >
            Tamam
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default SavedWordsMatch
